import React, { useState, useEffect } from 'react';
import {
  Pane,
  Card,
  Button,
  Text,
  Heading,
  Spinner,
  toaster,
  Dialog,
  RadioGroup,
  FormField,
  Alert,
  SelectMenu,
  Badge,
  Table,
  Strong,
  Icon,
  DownloadIcon,
  DocumentIcon,
  SearchIcon,
  FilterListIcon,
  TextInput,
  DesktopIcon,
  GlobeIcon
} from 'evergreen-ui';
import api from '../services/api';
import { useProject } from '../context/ProjectContext';

const Report = () => {
  const { currentProject } = useProject();
  const [loading, setLoading] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [reportConfig, setReportConfig] = useState({
    type: 'project',
    format: 'pdf',
    findingId: null
  });
  const [findings, setFindings] = useState([]);
  const [filteredFindings, setFilteredFindings] = useState([]);
  const [loadingFindings, setLoadingFindings] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('all');

  useEffect(() => {
    if (currentProject?.id) {
      fetchFindings();
    }
  }, [currentProject?.id]);

  useEffect(() => {
    filterFindings();
  }, [findings, searchTerm, selectedSeverity]);

  const fetchFindings = async () => {
    try {
      setLoadingFindings(true);
      const response = await api.get(`/projects/${currentProject.id}/results/findings`);
      
      // The backend now returns findings directly in the correct format
      if (response.data && response.data.findings) {
        const allFindings = response.data.findings.map(finding => ({
          ...finding,
          // Backend already provides source_type and source_name
          unique_key: `${finding.source_type}-${finding.id}`
        }));
        setFindings(allFindings);
      } else {
        setFindings([]);
      }
    } catch (error) {
      console.error('Error fetching findings:', error);
      toaster.danger('Failed to fetch findings for report generation');
      setFindings([]);
    } finally {
      setLoadingFindings(false);
    }
  };

  const filterFindings = () => {
    let filtered = findings.filter(finding => {
      const matchesSearch = !searchTerm || 
        finding.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        finding.source_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (finding.description && finding.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesSeverity = selectedSeverity === 'all' || finding.severity === selectedSeverity;
      
      return matchesSearch && matchesSeverity;
    });
    
    setFilteredFindings(filtered);
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': 'red',
      'high': 'orange',
      'medium': 'yellow',
      'low': 'green',
      'info': 'blue'
    };
    return colors[severity] || 'neutral';
  };

  const getSeverityBackgroundColor = (severity) => {
    const colors = {
      'critical': '#FFE8E6',
      'high': '#FFF4E6',
      'medium': '#FFFBF0',
      'low': '#E6F4EA',
      'info': '#E6F3FF'
    };
    return colors[severity] || '#F7F8FA';
  };

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        report_type: reportConfig.type,
        format: reportConfig.format
      });
      
      if (reportConfig.type === 'finding' && reportConfig.findingId) {
        params.append('finding_id', reportConfig.findingId);
      }
      
      const response = await api.post(
        `/reports/projects/${currentProject.id}/generate?${params.toString()}`,
        {},
        {
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const fileExtension = reportConfig.format;
      const reportTypeLabel = reportConfig.type === 'project' ? 'project' : 'finding';
      link.setAttribute('download', `${currentProject.name}_${reportTypeLabel}_report.${fileExtension}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toaster.success('Report generated and downloaded successfully');
      setShowGenerateDialog(false);
      
    } catch (error) {
      console.error('Error generating report:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to generate report';
      toaster.danger(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validateReportConfig = () => {
    if (reportConfig.type === 'finding' && !reportConfig.findingId) {
      return 'Please select a finding for the finding report';
    }
    return null;
  };

  if (!currentProject) {
    return (
      <Pane padding={16}>
        <Alert
          intent="warning"
          title="No project selected"
          marginBottom={16}
        >
          Please select a project first to generate reports.
        </Alert>
      </Pane>
    );
  }

  return (
    <Pane padding={16}>
      {/* Header */}
      <Pane 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between" 
        marginBottom={24}
      >
        <Heading size={800}>Security Reports</Heading>
        <Button
          appearance="primary"
          iconBefore={DocumentIcon}
          onClick={() => setShowGenerateDialog(true)}
          disabled={loadingFindings}
        >
          Generate Report
        </Button>
      </Pane>

      {/* Project Information */}
      <Card padding={16} marginBottom={24} background="tint1" border="default" elevation={1}>
        <Heading size={500} marginBottom={12}>Current Project</Heading>
        <Pane display="flex" alignItems="center" gap={16}>
          <Strong>{currentProject.name}</Strong>
          <Badge color="blue">{currentProject.id}</Badge>
        </Pane>
      </Card>

      {/* Available Findings for Individual Reports */}
      <Card padding={16} background="white" border="default" elevation={1}>
        <Pane 
          display="flex" 
          alignItems="center" 
          justifyContent="space-between" 
          marginBottom={16}
        >
          <Pane display="flex" alignItems="center" gap={12}>
            <Heading size={500}>Available Findings</Heading>
            {!loadingFindings && (
              <Badge backgroundColor="#F7F8FA" color="default">
                {findings.length} total
              </Badge>
            )}
            {loadingFindings && (
              <Pane display="flex" alignItems="center" gap={8}>
                <Spinner size={16} />
                <Text size={300} color="muted">Loading...</Text>
              </Pane>
            )}
          </Pane>
          <Pane display="flex" gap={8}>
            <TextInput
              placeholder="Search findings..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              width={200}
            />
            <SelectMenu
              title="Filter by Severity"
              options={[
                { label: 'All Severities', value: 'all' },
                { label: 'Critical', value: 'critical' },
                { label: 'High', value: 'high' },
                { label: 'Medium', value: 'medium' },
                { label: 'Low', value: 'low' },
                { label: 'Info', value: 'info' }
              ]}
              selected={selectedSeverity}
              onSelect={item => setSelectedSeverity(item.value)}
            >
              <Button iconBefore={FilterListIcon}>
                Severity: {selectedSeverity === 'all' ? 'All' : selectedSeverity.charAt(0).toUpperCase() + selectedSeverity.slice(1)}
              </Button>
            </SelectMenu>
            <Button
              size="small"
              appearance="minimal"
              iconBefore={DownloadIcon}
              onClick={fetchFindings}
              disabled={loadingFindings}
            >
              Refresh
            </Button>
          </Pane>
        </Pane>

        {loadingFindings ? (
          <Pane display="flex" alignItems="center" justifyContent="center" height={200}>
            <Pane display="flex" flexDirection="column" alignItems="center" gap={16}>
              <Spinner size={32} />
              <Text size={400} color="muted">Loading findings...</Text>
            </Pane>
          </Pane>
        ) : filteredFindings.length === 0 ? (
          <Pane
            display="flex"
            alignItems="center"
            justifyContent="center"
            height={200}
            background="tint1"
            borderRadius={4}
          >
            <Pane textAlign="center">
              <Text size={500} color="muted" marginBottom={8}>
                {findings.length === 0 ? 'No findings available for individual reports' : 'No findings match your search criteria'}
              </Text>
              {findings.length === 0 && (
                <Text size={300} color="muted">
                  Findings will appear here once security scans are completed
                </Text>
              )}
            </Pane>
          </Pane>
        ) : (
          <Pane overflowX="auto">
            <Table>
            <Table.Head>
              <Table.TextHeaderCell width="35%">
                Finding Name {filteredFindings.length > 0 && `(${filteredFindings.length})`}
              </Table.TextHeaderCell>
              <Table.TextHeaderCell width="12%">Severity</Table.TextHeaderCell>
              <Table.TextHeaderCell width="20%">Source</Table.TextHeaderCell>
              <Table.TextHeaderCell width="20%">Target</Table.TextHeaderCell>
              <Table.TextHeaderCell width="13%">Actions</Table.TextHeaderCell>
            </Table.Head>
            <Table.Body>
              {filteredFindings.map((finding) => (
                <Table.Row key={finding.unique_key || finding.id}>
                  <Table.Cell width="35%">
                    <Pane maxWidth="100%" overflow="hidden">
                      <Strong 
                        display="block" 
                        marginBottom={4}
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        title={finding.name}
                      >
                        {finding.name}
                      </Strong>
                      {finding.description && (
                        <Text 
                          size={300} 
                          color="muted" 
                          display="block"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          title={finding.description}
                        >
                          {finding.description.length > 80 
                            ? `${finding.description.substring(0, 80)}...` 
                            : finding.description
                          }
                        </Text>
                      )}
                    </Pane>
                  </Table.Cell>
                  <Table.Cell width="12%">
                    <Badge
                      backgroundColor={getSeverityBackgroundColor(finding.severity)}
                      color={finding.severity === 'critical' ? 'red' : 'default'}
                    >
                      {finding.severity?.toUpperCase() || 'NONE'}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell width="20%">
                    <Pane display="flex" alignItems="center" gap={8} maxWidth="100%">
                      {finding.source_type === 'host' ? (
                        <DesktopIcon size={12} />
                      ) : (
                        <GlobeIcon size={12} />
                      )}
                      <Text 
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        title={finding.source_name}
                        flex={1}
                      >
                        {finding.source_name}
                      </Text>
                    </Pane>
                  </Table.Cell>
                  <Table.Cell width="20%">
                    <Text 
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      title={finding.url || finding.port?.number || 'N/A'}
                    >
                      {finding.url || (finding.port ? `${finding.port.number}/${finding.port.protocol}` : 'N/A')}
                    </Text>
                  </Table.Cell>
                  <Table.Cell width="13%">
                    <Button
                      size="small"
                      iconBefore={DocumentIcon}
                      onClick={() => {
                        setReportConfig({
                          type: 'finding',
                          format: 'pdf',
                          findingId: finding.id
                        });
                        setShowGenerateDialog(true);
                      }}
                    >
                      Report
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          </Pane>
        )}
      </Card>

      {/* Report Generation Dialog */}
      <Dialog
        isShown={showGenerateDialog}
        title="Generate Security Report"
        onCloseComplete={() => {
          setShowGenerateDialog(false);
          setReportConfig({
            type: 'project',
            format: 'pdf',
            findingId: null
          });
        }}
        confirmLabel="Generate Report"
        isConfirmLoading={loading}
        onConfirm={handleGenerateReport}
        isConfirmDisabled={!!validateReportConfig()}
        width={600}
      >
        <Pane>
          <FormField
            label="Report Type"
            description="Choose the scope of your security report"
            marginBottom={16}
          >
            <RadioGroup
              value={reportConfig.type}
              options={[
                {
                  label: 'Project Report',
                  value: 'project',
                  description: 'Complete security assessment of the entire project including executive summary, network discovery, and all vulnerabilities'
                },
                {
                  label: 'Individual Finding Report',
                  value: 'finding',
                  description: 'Detailed report for a specific vulnerability with enhanced AI-generated descriptions and impact analysis'
                }
              ]}
              onChange={(event) => {
                const value = event.target ? event.target.value : event;
                console.log('Report type changed to:', value);
                setReportConfig({
                  ...reportConfig,
                  type: value,
                  findingId: value === 'project' ? null : reportConfig.findingId
                });
              }}
            />
          </FormField>

          {reportConfig.type === 'finding' && (
            <FormField
              label="Select Finding"
              description="Choose the specific finding to generate a report for"
              marginBottom={16}
            >
              <SelectMenu
                title="Select a finding"
                options={findings.map(finding => ({
                  label: `${finding.name} - ${finding.severity?.toUpperCase() || 'NONE'} (${finding.source_type}: ${finding.source_name})`,
                  value: finding.id
                }))}
                selected={reportConfig.findingId}
                onSelect={item => setReportConfig(prev => ({ ...prev, findingId: item.value }))}
                hasFilter={false}
                height={200}
              >
                <Button width="100%">
                  {reportConfig.findingId 
                    ? findings.find(f => f.id === reportConfig.findingId)?.name || 'Select Finding'
                    : 'Select Finding'
                  }
                </Button>
              </SelectMenu>
            </FormField>
          )}

          <FormField
            label="Report Format"
            description="Choose the output format for your report"
            marginBottom={16}
          >
            <RadioGroup
              value={reportConfig.format}
              options={[
                {
                  label: 'PDF',
                  value: 'pdf',
                  description: 'Professional PDF format suitable for sharing and presentations'
                },
                {
                  label: 'DOCX',
                  value: 'docx',
                  description: 'Microsoft Word format for further editing and customization'
                }
              ]}
              onChange={(event) => {
                const value = event.target ? event.target.value : event;
                console.log('Report format changed to:', value);
                setReportConfig({
                  ...reportConfig,
                  format: value
                });
              }}
            />
          </FormField>

          {validateReportConfig() && (
            <Alert
              intent="warning"
              title="Configuration Required"
              marginTop={16}
            >
              {validateReportConfig()}
            </Alert>
          )}

          <Alert
            intent="info"
            title="AI-Enhanced Reports"
            marginTop={16}
          >
            Reports are enhanced with AI-generated vulnerability descriptions, impact assessments, 
            and remediation recommendations using your OpenRouter API key. Make sure you have 
            configured your API key in your profile settings.
          </Alert>
        </Pane>
      </Dialog>
    </Pane>
  );
};

export default Report;